import { queryOptions } from '@tanstack/react-query';
import { captureCallSession, callKey, callUUID, currentCallSession } from './call-session';
import { activeCallQuery, agentCallQuery, readOwnedCallPage, readOwnedCallStats, readOwnedCallDetail } from './call-reads';
import { CallAPIError } from './call-contract';
import type {CallPageInput} from './call-page-contract';

export type { Call, CallParticipant } from './call-view';
export { useStartCall, useAnswerCall, useRejectCall, useEndCall, useCallToken } from './call-actions';
export type { StartCallArgs } from './call-actions';
export interface CallsFilter {
 direction: 'all'|'inbound'|'outbound'|'missed';
 range: 'today'|'week'|'month'|'all';
 search: string;
}
export interface AgentSummary { id: string; name: string; email?: string; avatar_url?: string; online?: boolean; lastSeen?: string|null }
export interface CallStats { total: number; missed: number; active: number; avgDurationSec: number|null }
export interface CallTokenResponse {token:string;url:string;room_name:string}
export function callRangeFrom(range:CallsFilter['range'],now=Date.now()):string|null{
 if(range==='all')return null;const date=new Date(now);
 if(range==='today')date.setHours(0,0,0,0);else date.setDate(date.getDate()-(range==='week'?7:30));
 return date.toISOString();
}
function listQuery(filter:CallsFilter,currentUserId?:string,pagination?:{cursor:string;createdFrom:string|null;limit?:number}){
 const owner=captureCallSession(),input:CallPageInput={direction:filter.direction,search:filter.search.trim(),limit:pagination?.limit??50,cursor:pagination?.cursor??'',createdFrom:pagination?pagination.createdFrom:callRangeFrom(filter.range)};
 const range=filter.range;
 return queryOptions({queryKey:[...callKey('history',owner),{...input,range,createdFrom:input.cursor?input.createdFrom:null},input.search?owner.language:null],enabled:callUUID(owner.user)&&callUUID(owner.tenant),retry:false,staleTime:15_000,refetchInterval:60_000,
 queryFn:async({signal,client})=>{currentCallSession(owner,signal);if(currentUserId&&currentUserId!==owner.user)throw new CallAPIError('unauthorized');
 // The first page's visible poll follows the current rolling period. Following
 // cursors keep that page's cutoff so browsing does not drift between requests.
 const snapshot={...input,createdFrom:input.cursor?input.createdFrom:callRangeFrom(range)};
 const page=await readOwnedCallPage(owner,snapshot,signal);currentCallSession(owner,signal);
 for(const row of page.calls)client.setQueryData([...callKey('detail',owner),row.id],row);
 return page;}});
}
export const callQueries={
 history:()=>listQuery({direction:'all',range:'all',search:''}),
 list:listQuery,
 byId:(id:string|undefined)=>{
  const owner=captureCallSession();
  return queryOptions({queryKey:[...callKey('detail',owner),id],enabled:callUUID(id)&&callUUID(owner.user)&&callUUID(owner.tenant),retry:false,staleTime:15_000,refetchInterval:60_000,
  queryFn:({signal})=>{currentCallSession(owner,signal);if(!callUUID(id))throw new CallAPIError('bad_input');return readOwnedCallDetail(owner,id,signal);}});
 },
 active:activeCallQuery,agents:agentCallQuery,
 stats:(currentUserId?:string)=>{
  const owner=captureCallSession();return queryOptions({queryKey:callKey('stats',owner),enabled:callUUID(owner.user)&&callUUID(owner.tenant),retry:false,staleTime:15_000,refetchInterval:60_000,
  queryFn:({signal})=>{currentCallSession(owner,signal);if(currentUserId&&currentUserId!==owner.user)throw new CallAPIError('unauthorized');return readOwnedCallStats(owner,signal);}});
 },
};
